import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { TAGS } from "./constants";
import { LuFileSpreadsheet } from "react-icons/lu";
import { fetchTimelogsByDateRange } from "./services";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const SummaryView = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [timelogs, setTimelogs] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem("summary_range");
    if (saved) {
      const { startDate, endDate } = JSON.parse(saved);
      setStartDate(startDate);
      setEndDate(endDate);
      applyDateRange(startDate, endDate);
    } else {
      // Default to current month
      const sDate = moment().startOf("month").format("YYYY-MM-DD");
      const eDate = moment().endOf("month").format("YYYY-MM-DD");
      setStartDate(sDate);
      setEndDate(eDate);
      applyDateRange(sDate, eDate);
    }
  }, []);

  const { onlyTimelogs, tagBreakdown, byDate, sortedDates } = useMemo(() => {
    const onlyTimelogs = timelogs.filter((l) => l.type === "timelog");
    const tagBreakdown = {};

    TAGS.forEach((tag) => {
      tagBreakdown[tag.id] = onlyTimelogs
        .filter((l) => l.tag === tag.id)
        .reduce((sum, l) => sum + l.duration, 0);
    });

    const byDate = {};
    onlyTimelogs.forEach((l) => {
      if (!byDate[l.date]) byDate[l.date] = [];
      byDate[l.date].push(l);
    });

    const sortedDates = Object.keys(byDate).sort();

    return { onlyTimelogs, tagBreakdown, byDate, sortedDates };
  }, [timelogs]);

  const applyDateRange = async (startDate, endDate) => {
    if (!startDate || !endDate) return alert("Select both dates");
    localStorage.setItem(
      "summary_range",
      JSON.stringify({ startDate, endDate })
    );
    const data = await fetchTimelogsByDateRange(startDate, endDate);
    console.log(data, "data");
    setTimelogs(data);
  };

  // EXPORT CSV
  const exportToCSV = () => {
    if (!onlyTimelogs.length) return alert("No timelogs to export");

    // Group logs by date (same as table)
    const byDate = {};
    onlyTimelogs.forEach((log) => {
      if (!byDate[log.date]) byDate[log.date] = [];
      byDate[log.date].push(log);
    });

    const sortedDates = Object.keys(byDate).sort();

    const csv = [
      ["Date", "Tasks", "Tags Used", "Hours", "Logs Count"], // header row
      ...sortedDates.map((date) => {
        const logs = byDate[date];

        const tasks = logs.map((l) => l.title).join(" | "); // combine tasks

        const uniqueTags = [...new Set(logs.map((l) => l.tag))];
        const tagLabels = uniqueTags
          .map((tagId) => TAGS.find((t) => t.id === tagId)?.label || tagId)
          .join(" | "); // combine tags

        const totalHours = logs
          .reduce((sum, l) => sum + l.duration, 0)
          .toFixed(1);

        return [date, tasks, tagLabels, totalHours, logs.length];
      }),
    ]
      .map((row) => row.map((c) => `"${c}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `timelogs-${startDate}-to-${endDate}.csv`;
    a.click();
  };

  // Dynamic title
  const formattedTitle =
    startDate && endDate
      ? `${moment(startDate).format("MMM DD YYYY")} → ${moment(endDate).format(
          "MMM DD YYYY"
        )}`
      : "";

  // === PIE CHART DATA ===
  const getPieChartData = () => {
    return TAGS.map((tag) => ({
      name: tag.label,
      value: tagBreakdown[tag.id] || 0,
      color: tag.color,
    })).filter((item) => item.value > 0);
  };

  // ==================== UI FUNCTIONS ====================

  const displayHeaderUI = () => (
    <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div className="flex gap-4 flex-wrap items-end">
        <div className="flex flex-col">
          <label className="text-xs text-slate-600 mb-1">From</label>
          <input
            type="date"
            className="border rounded px-3 py-2 text-sm w-44 h-[38px]"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs text-slate-600 mb-1">To</label>
          <input
            type="date"
            className="border rounded px-3 py-2 text-sm w-44 h-[38px]"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button
          onClick={() => {
            applyDateRange(startDate, endDate);
          }}
          className="rounded-md bg-blue-600 px-4 py-2 w-full md:w-fit text-sm font-medium text-white hover:bg-blue-700 h-[38px] flex items-center"
        >
          Apply
        </button>
      </div>

      {/* Export Button */}
      <button
        onClick={exportToCSV}
        className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 flex gap-2 items-center h-[38px]"
      >
        <LuFileSpreadsheet size={18} /> Export CSV
      </button>
    </div>
  );

  const displaySubHeaderUI = () => (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="text-xl font-bold text-slate-900">{formattedTitle}</h2>
      <p className="text-sm text-slate-600 mt-1">
        Showing summary for selected range
      </p>
    </div>
  );

  const displayPieChartUI = () => {
    const data = getPieChartData();

    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-4">
          Work Breakdown Overview
        </h3>

        {data.length === 0 ? (
          <p className="text-slate-500 text-sm">No data available</p>
        ) : (
          <div className="grid md:grid-cols-4 gird-cols-1 md:items-center gap-6">
            <div className="col-span-3 flex justify-center">
              <ResponsiveContainer width="100%" height={360}>
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={160}
                    innerRadius={60}
                    paddingAngle={2}
                  >
                    {data.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-3">
              {data.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="flex-1 text-sm text-slate-700">
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const displayRightStatsUI = () => {
    const totalHours = timelogs
      .filter((l) => l.type === "timelog")
      .reduce((s, l) => s + l.duration, 0);

    const totalLogs = timelogs.filter((l) => l.type === "timelog").length;
    const leaveCount = timelogs.filter((l) => l.type === "leave").length;

    return (
      <div className="grid grid-cols-1 gap-4">
        {/* Total Hours */}
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-600">Total Hours</p>
          <p className="mt-2 text-3xl font-bold text-blue-600">
            {totalHours.toFixed(1)}h
          </p>
        </div>

        {/* Total Logs */}
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-600">Total Logs</p>
          <p className="mt-2 text-3xl font-bold text-purple-600">{totalLogs}</p>
        </div>

        {/* Leave Days */}
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-600">Total Leaves</p>
          <p className="mt-2 text-3xl font-bold text-red-500">{leaveCount}</p>
        </div>
      </div>
    );
  };

  const displayTableUI = () => (
    <div className="rounded-lg bg-white p-6 shadow-sm overflow-x-auto">
      <h3 className="mb-4 font-bold text-slate-900">Daily Summary</h3>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            <th className="text-left py-3 px-3 font-semibold text-slate-700">
              Date
            </th>
            <th className="text-left py-3 px-3 font-semibold text-slate-700">
              Tasks
            </th>
            <th className="text-left py-3 px-3 font-semibold text-slate-700">
              Tags Used
            </th>
            <th className="text-right py-3 px-3 font-semibold text-slate-700">
              Hours
            </th>
            <th className="text-center py-3 px-3 font-semibold text-slate-700">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {sortedDates.length === 0 ? (
            <tr>
              <td colSpan="5" className="py-8 text-center text-slate-500">
                No logs for this month
              </td>
            </tr>
          ) : (
            sortedDates.map((date) => {
              const logs = byDate[date];
              const dateHours = logs.reduce(
                (sum, log) => sum + log.duration,
                0
              );
              const uniqueTags = [...new Set(logs.map((log) => log.tag))];

              return (
                <tr
                  key={date}
                  className="border-b border-slate-100 hover:bg-slate-50 transition"
                >
                  <td className="py-3 px-3 text-slate-700 font-medium align-top">
                    {date}
                  </td>

                  <td className="py-3 px-3 align-top">
                    <div className="space-y-1">
                      {logs.map((log, i) => (
                        <div key={log.id} className="text-xs text-slate-700">
                          {i + 1}. {log.title}
                        </div>
                      ))}
                    </div>
                  </td>

                  <td className="py-3 px-3 align-top">
                    <div className="flex flex-wrap gap-1 items-start">
                      {uniqueTags.map((tagId) => {
                        const tag = TAGS.find((t) => t.id === tagId);
                        return (
                          <span
                            key={tagId}
                            className="text-xs font-medium rounded px-2 py-1 text-white"
                            style={{ backgroundColor: tag?.color }}
                          >
                            {tag?.label}
                          </span>
                        );
                      })}
                    </div>
                  </td>

                  <td className="py-3 px-3 text-right font-medium text-slate-900 align-top">
                    {dateHours.toFixed(1)}h
                  </td>

                  <td className="py-3 px-3 text-center align-top">
                    {logs.length > 0 && (
                      <span className="text-xs text-slate-500">
                        {logs.length} logs
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  // ==================== FINAL RENDER ====================
  return (
    <div className="space-y-6">
      {displayHeaderUI()}
      {displaySubHeaderUI()}

      {/* PIE + Stats */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-6">
        {displayPieChartUI()}
        {displayRightStatsUI()}
      </div>

      {displayTableUI()}
    </div>
  );
};

export default SummaryView;
